
const ADMIN_TOKEN_SESSION_KEY = 'bramx-admin-token-session';
export const getAdminToken = (): string => typeof window === 'undefined' ? '' : (window.sessionStorage.getItem(ADMIN_TOKEN_SESSION_KEY) ?? '');
export const hasAdminToken = (): boolean => Boolean(getAdminToken());
export const setAdminToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  const value = token.trim();
  if (value) window.sessionStorage.setItem(ADMIN_TOKEN_SESSION_KEY, value);
  else window.sessionStorage.removeItem(ADMIN_TOKEN_SESSION_KEY);
  window.dispatchEvent(new Event('bramx-admin-token-changed'));
};
export const clearAdminToken = (): void => setAdminToken('');
const adminHeaders = (): Record<string, string> => {
  const token = getAdminToken();
  return token ? { 'x-admin-token': token } : {};
};

export const isTransientNetworkError = (error: unknown): boolean =>
  error instanceof TypeError || (error instanceof Error && /network|fetch|internet|connection/i.test(error.message));

export async function fetchWithTransientRetry(input: RequestInfo | URL, init?: RequestInit, attempts = 3): Promise<Response> {
  let lastError: unknown = null;
  const total = Math.max(1, Math.floor(attempts));
  for (let attempt = 0; attempt < total; attempt += 1) {
    try {
      return await fetch(input, init);
    } catch (error) {
      lastError = error;
      if (!isTransientNetworkError(error) || attempt === total - 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 300 : 1000));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('NETWORK_RETRY_EXHAUSTED');
}

export interface PublicConfigResponse {
  timezone: string;
  defaultEntries: { bram: number | null; viktor: number | null };
  defaultLeagueId: number;
  overallLeagueId: number;
  simIterations: number;
  dataMode: 'free' | 'sportmonks';
  freeEnrichment: { enabled: boolean; apiFootballConfigured: boolean; espnLiveEdgeEnabled?: boolean };
  freeIntelligence?: { footballData: boolean; oddsApiConfigured: boolean; vaastav: boolean; understatCached?: boolean; fplcacheLearning: boolean };
}

export interface FplEventBrief {
  id: number;
  name: string;
  deadline_time: string;
  finished: boolean;
  data_checked: boolean;
}

export interface CurrentGameweekResponse {
  gameweek: number;
  event: FplEventBrief | null;
  nextGameweek: number | null;
  nextEvent: FplEventBrief | null;
  previousGameweek: number | null;
  autoScope: 'live' | 'preview';
  stateSource: 'FPL_FLAG' | 'DEADLINE_CLOCK' | 'FALLBACK';
  deadlinePassed: boolean;
  serverNow: string;
}

export interface BootstrapResponse {
  teams: Array<{ id: number; name: string; short_name: string }>;
  elements: Array<{ id: number; first_name: string; second_name: string; web_name: string; team: number; element_type?: number; now_cost?: number; status?: string }>;
}

export interface Fixture {
  id: number;
  event: number | null;
  finished: boolean;
  kickoff_time: string | null;
  minutes?: number;
  started?: boolean;
  team_h: number;
  team_a: number;
  team_h_score: number | null;
  team_a_score: number | null;
}

export interface ComparedPlayer {
  elementId: number;
  firstName: string;
  secondName: string;
  webName: string;
  teamId: number;
  teamShortName: string;
  elementType: number;
  positionA: number | null;
  positionB: number | null;
  livePoints: number;
  multiplierA: number;
  multiplierB: number;
  captainA: boolean;
  captainB: boolean;
  viceCaptainA: boolean;
  viceCaptainB: boolean;
  minutesPlayed: number;
  goals: number;
  assists: number;
  cleanSheets: number;
  saves: number;
  bonus: number;
  bps: number;
  yellowCards: number;
  redCards: number;
  actualStarted: boolean | null;
  liveXg: number | null;
  liveXa: number | null;
  liveXgi: number | null;
  liveXgc: number | null;
  selectedByPercent: number | null;
  form: number | null;
  pointsPerGame: number | null;
  price: number | null;
  transferNetEvent: number;
  xgPer90: number | null;
  xaPer90: number | null;
  xgiPer90: number | null;
  xgcPer90: number | null;
  fixtureDifficulty: number | null;
  startsRate: number | null;
  modelSignals: {
    benchmarkEp: number | null;
    form: number | null;
    pointsPerGame: number | null;
    xgPer90: number | null;
    xaPer90: number | null;
    xgiPer90: number | null;
    xgcPer90: number | null;
    startsRate: number | null;
    fixtureDifficulty: number | null;
    transferMomentum: number | null;
    transferNetEvent: number;
    price: number | null;
    availability: number;
    currentPointsPer90: number | null;
    priorPointsPer90: number | null;
    priorXgiPer90: number | null;
    priorStartsRate: number | null;
    currentSeasonEvidenceWeight: number;
    benchmarkWeight: number;
    ownFootballEstimate: number;
    bramxCoreXpts: number;
    fplBenchmarkXpts: number | null;
    calibratedEnsembleXpts: number | null;
    calibrationStatus: 'PENDING';
    marketAdjustment: number;
    ensembleAdjustment: number;
    model: 'BRAMX_CORE_XPTS_V0_3_7';
  };
  minuteDistribution: { pStart: number; pZero: number; pCameo: number; p60Plus: number; p75Plus: number; p90: number; expectedMinutes: number; source: string[]; confidence: 'A'|'B'|'C'|'D' };
  dcState: { currentCount: number; threshold: 10|12; actionsNeeded: number; prematchRate90: number | null; expectedRemainingActions: number | null; pThreshold: number | null };
  roleState: { roleLabel: string; roleStability: number; penaltyShare: number | null; directFreeKickShare: number | null; cornerShare: number | null; managerRotationRisk: number | null; evidence: string[]; status: 'MODEL-EST'|'PENDING' };
  componentXptsBeta?: R6ComponentXptsBetaDiagnostic;
  confidence: { dataCompleteness: 'A'|'B'|'C'|'D'; informationConfidence: 'A'|'B'|'C'|'D'; modelConfidence: 'A'|'B'|'C'|'D' };
  projectionSources: string[];
  preMatchXpts: number;
  expectedRemaining: number;
  projectedPlayerPoints: number;
  appearanceProbability: number;
  projectionLabel: 'MODEL-EST';
  projectionConfidence: 'A' | 'B' | 'C' | 'D';
  fixtureStatus: 'FINISHED' | 'LIVE' | 'UPCOMING' | 'NO_FIXTURE';
  fixtureMinute: number | null;
  fixtureProjections: Array<{
    fixtureId: number;
    opponentTeamId: number;
    opponentShortName: string;
    status: 'FINISHED' | 'LIVE' | 'UPCOMING' | 'NO_FIXTURE';
    minute: number | null;
    kickoffAt: string | null;
    appearanceProbability: number;
    expectedMinutes: number;
    expectedRemaining: number;
    marketSource: string | null;
    teamExpectedGoals: number | null;
    cleanSheetProbability: number | null;
  }>;
}

export interface EntrySummary {
  id: number;
  managerName: string;
  teamName: string;
  officialEntryEventPoints: number;
  overallPoints: number;
  overallRank: number | null;
  provisionalPointsFromPublishedPicks: number;
  expectedRemaining: number;
  projectedFinalPoints: number;
  captainElementId: number | null;
  viceCaptainElementId: number | null;
  benchElementIds: number[];
  activeChip: string | null;
  transferHitCost: number;
}

export interface CompareResponse {
  gameweek: number;
  targetGameweek: number;
  picksBasisGameweek: number;
  scope: 'LIVE' | 'NEXT_PREVIEW';
  picksBasis: 'PUBLISHED_TARGET_GW' | 'LAST_PUBLISHED_SQUAD';
  entryABasis: 'PUBLISHED_TARGET_GW' | 'LAST_PUBLISHED_SQUAD' | 'PLANNED';
  entryBBasis: 'PUBLISHED_TARGET_GW' | 'LAST_PUBLISHED_SQUAD' | 'PLANNED';
  plannedRouteLabel: string | null;
  plannedTransferHitCost: number;
  plannedTransfersMade: number;
  plannedFreeTransfersRemaining: number | null;
  dataSources: Array<{ name: string; status: string; detail: string }>;
  deadlineTime: string | null;
  generatedAt: string;
  pointsMethod: string;
  autosubsApplied: false;
  projectionMethod: string;
  entryA: EntrySummary;
  entryB: EntrySummary;
  shared: ComparedPlayer[];
  onlyA: ComparedPlayer[];
  onlyB: ComparedPlayer[];
  players: ComparedPlayer[];
}

export interface SimSideSummary {
  mean: number;
  median: number;
  p10: number;
  p90: number;
  winProbability: number;
}

export interface MatchSimulation {
  fixtureId: number;
  scope: 'MATCH';
  iterations: number;
  meanEdgeToA: number;
  medianEdgeToA: number;
  p10EdgeToA: number;
  p90EdgeToA: number;
  aEdgeProbability: number;
  bEdgeProbability: number;
  drawProbability: number;
  note: string;
}

export interface EdgeWaterfallItem {
  elementId: number;
  playerName: string;
  teamShortName: string;
  reasonCode: string;
  deltaToA: number;
  previousEdge: number;
  currentEdge: number;
  detail: string;
}

export interface BramxCompareResponse extends CompareResponse {
  simulation: {
    method: 'monte_carlo';
    scope: 'GAMEWEEK';
    label: 'SIM' | 'CALIBRATED SIM';
    displayLabel: string;
    modelVersion: string;
    iterations: number;
    seed: string;
    inputSnapshotHash: string | null;
    executedAt: string;
    inputQuality: 'MODEL-EST';
    calibration: { status: 'PENDING' | 'CALIBRATED'; version: string | null };
    gameweek: {
      a: SimSideSummary;
      b: SimSideSummary;
      drawProbability: number;
      autosubModel: string;
      captainFallback: true;
    };
    a: SimSideSummary;
    b: SimSideSummary;
    drawProbability: number;
    matches: MatchSimulation[];
    assumptions: string[];
  };
  waterfall: {
    mode: 'BASELINE' | 'DELTA';
    observedAt: string;
    previousObservedAt: string | null;
    netDeltaToA: number;
    items: EdgeWaterfallItem[];
  };
  replay: {
    simulationRunId: string | null;
    randomSeed: string;
    inputSnapshotHash: string;
    modelVersion: string;
    exactReplayReady: boolean;
  };
  predictionFreezes: {
    captured: Array<{ fixtureId: string; freezeType: 'T_MINUS_60' | 'KICKOFF' | 'FULL_TIME'; capturedAt: string }>;
    states: Array<{ fixtureId: string; freezeType: 'T_MINUS_60' | 'KICKOFF' | 'FULL_TIME'; capturedAt: string; targetAt: string }>;
  };
}

export interface FeedHealthResponse {
  generatedAt: string;
  feeds: Array<{
    name: string;
    status: string;
    freshnessSeconds: number | null;
    lastSuccessAt: string | null;
    message?: string;
  }>;
}

export interface LeagueStanding {
  entry: number;
  entry_name: string;
  player_name: string;
  rank: number;
  last_rank: number;
  total: number;
  event_total: number;
  movement?: string;
  own_entry?: boolean;
}

export interface LeagueStandingsResponse {
  league: { id: number; name: string; start_event?: number };
  entries: LeagueStanding[];
  count: number;
  limit: number;
  phase: number;
  lastUpdatedData: string | null;
  source: string;
  generatedAt: string;
}

export interface ProviderParticipant {
  id: string;
  name: string;
  location: 'home' | 'away' | null;
  imagePath: string | null;
}

export interface ProviderFormation {
  id: string;
  participantId: string;
  location: 'home' | 'away' | null;
  formation: string | null;
}

export interface ProviderLineup {
  id: string;
  playerId: string;
  teamId: string;
  playerName: string;
  jerseyNumber: number | null;
  positionId: number | null;
  formationPosition: number | null;
  formationField: string | null;
  captain: boolean;
  starter: boolean | null;
}

export interface ProviderEvent {
  id: string;
  type: string;
  typeId: number | null;
  minute: number | null;
  extraMinute: number | null;
  teamId: string | null;
  playerName: string | null;
  relatedPlayerName: string | null;
  result: string | null;
  info: string | null;
  x: number | null;
  y: number | null;
}

export interface ProviderStatistic {
  id: string;
  typeName: string | null;
  location: string | null;
  participantId: string | null;
  value: unknown;
}

export interface ProviderPlayerStatistic {
  id: string;
  playerId: string;
  teamId: string;
  playerName: string;
  minutes: number | null;
  position: string | null;
  rating: number | null;
  captain: boolean;
  substitute: boolean;
  shotsTotal: number | null;
  shotsOn: number | null;
  goals: number | null;
  assists: number | null;
  keyPasses: number | null;
  tackles: number | null;
  interceptions: number | null;
  duelsWon: number | null;
  yellowCards: number | null;
  redCards: number | null;
}

export interface StreamFreshness {
  stream: 'score' | 'events' | 'lineups' | 'statistics' | 'xg' | 'ballCoordinates';
  status: 'LIVE' | 'STALE' | 'IDLE' | 'UNAVAILABLE';
  ageSeconds: number | null;
  slaSeconds: number;
  basis: 'provider-fixture-timestamp' | 'received-at';
  sourceUpdatedAt: string | null;
  observedAt: string;
}

export interface ProviderReconciliation {
  fixtureId: string;
  checkedAt: string;
  baseline: boolean;
  revisions: Array<{
    action: 'ADDED' | 'CORRECTED' | 'REMOVED';
    eventId: string;
    eventType: string;
    minute: number | null;
    playerName: string | null;
    summary: string;
  }>;
  counts: { added: number; corrected: number; removed: number };
}

export interface ProviderFixtureBundle {
  fixture: {
    id: string;
    status: string;
    minute: number | null;
    homeScore: number | null;
    awayScore: number | null;
    sourceProvider: string;
    sourceUpdatedAt: string | null;
    receivedAt: string;
  };
  leagueId: number | null;
  seasonId: number | null;
  name: string | null;
  startingAt: string | null;
  participants: ProviderParticipant[];
  formations: ProviderFormation[];
  events: ProviderEvent[];
  lineups: ProviderLineup[];
  statistics: ProviderStatistic[];
  playerStatistics: ProviderPlayerStatistic[];
  xg: ProviderStatistic[];
  pressure: unknown;
  ballCoordinates: unknown;
  freshness: Record<'score' | 'events' | 'lineups' | 'statistics' | 'xg' | 'ballCoordinates', StreamFreshness>;
  reconciliation?: ProviderReconciliation;
  capabilities: {
    lineups: boolean;
    formations: boolean;
    events: boolean;
    statistics: boolean;
    playerStatistics: boolean;
    xGFixture: boolean;
    pressure: boolean;
    ballCoordinates: boolean;
  };
  dataMode?: 'FREE';
  enrichment?: { status: string; message: string };
}

export interface MatchCentreResponse {
  provider: string;
  dataMode?: 'FREE' | 'SPORTMONKS';
  league: string;
  leagueId?: number;
  generatedAt: string;
  sourceMode?: string;
  enrichment?: { provider: string; configured: boolean; enabled: boolean; policy: string };
  live: ProviderFixtureBundle[];
  schedule: ProviderFixtureBundle[];
}

export interface PlannedSquadPayload {
  gameweek: number;
  activeChip?: string | null;
  initialFtCount?: number | null;
  ftCount?: number | null;
  transfersMade?: number | null;
  hitCost?: number | null;
  cashItb?: number | null;
  routeLabel?: string | null;
  picks: Array<{ element: number; position: number; isCaptain: boolean; isViceCaptain: boolean; multiplier?: number; purchasePrice?: number | null; sellingPrice?: number | null }>;
}

export interface PlannedSquadStateRevisionResponse {
  version: 'PlannedSquadStateRevision.v1' | 'LEGACY_UNVERSIONED';
  revisionId: string;
  auditLogId: string;
  asOf: string;
  recordedAt: string;
  provenance: 'ADMIN_PRIVATE_USER_AUTHORED' | 'ADMIN_PRIVATE_USER_AUTHORED_CLEAR' | 'USER_AUTHORITATIVE_PRIVATE_STATE_SYNC' | 'LEGACY_SERVER_AUDIT_LOG';
  supersedesRevisionId: string | null;
  sourceRef: string | null;
}

export interface PlannedSquadServerResponse {
  configured: boolean;
  plan: PlannedSquadPayload | null;
  savedAt: string | null;
  source: 'SERVER_AUDIT_LOG' | 'LOCAL_ONLY';
  stateRevision: PlannedSquadStateRevisionResponse | null;
}

export type R6ComponentChannelStatus =
  | 'VALUED'
  | 'VALUED_PARTIAL_INPUT'
  | 'VALUED_AGGREGATE_BASELINE'
  | 'NOT_APPLICABLE'
  | 'PENDING_MISSING_INPUT'
  | 'PENDING_NOT_VALUED'
  | 'BLOCKED_AGGREGATE_NOT_SEPARATED'
  | 'PENDING_UNSUPPORTED_MULTIPLE_FIXTURES';

export interface R6ComponentXptsBetaDiagnostic {
  version: 'ComponentXptsBeta.v2';
  status: 'BETA_CAUSAL_PARTIAL';
  elementId: number;
  teamId: number;
  elementType: number;
  targetGameweek: number;
  totalExpectedPoints: number;
  arithmeticSum: number;
  arithmeticClosure: true;
  totalSemantics: 'CAUSAL_BETA_PARTIAL_NOT_DECISION_GRADE';
  components: Array<{
    key: 'APPEARANCE'|'OPEN_PLAY_GOALS'|'OPEN_PLAY_ASSISTS'|'CLEAN_SHEET'|'GOALS_CONCEDED'|'DEFENSIVE_CONTRIBUTION'|'KEEPER_SAVES'|'NEGATIVE_EVENTS'|'BONUS'|'SPECIAL_ROLES';
    expectedPoints: number;
    status: R6ComponentChannelStatus;
    detail: string;
    sourceRefs: string[];
  }>;
  inputAudit: {
    minutes: { sources: string[]; confidence: 'A'|'B'|'C'|'D' };
    dc: { sources: string[]; confidence: 'A'|'B'|'C'|'D' };
    roleInputs: Array<{ causalField: string; truthStatus: string; r6Eligible: boolean; provenanceRefs: string[] }>;
  };
  specialRoles: { valuationStatus: 'PENDING_NOT_VALUED' } & Record<string, unknown>;
  unmodeledComponents: string[];
  calibrated: false;
  decisionGrade: false;
}

export interface HorizonPlayerGameweekProjection {
  gameweek: number;
  xPts: number;
  expectedMinutes: number;
  pStart: number;
  p60: number;
  p75: number;
  p90: number;
  informationConfidence: 'A'|'B'|'C'|'D';
  modelConfidence: 'A'|'B'|'C'|'D';
  roleState: { roleLabel: string; roleStability: number; status: 'MODEL-EST'|'PENDING' };
  componentXptsBeta?: R6ComponentXptsBetaDiagnostic;
  fixtureIds: number[];
  restDays: number | null;
  marketStatus: 'MARKET'|'FALLBACK';
  uncertaintyScale: number;
  label: 'MODEL-EST';
}

export interface HorizonProjectionResponse {
  status: 'PARTIAL';
  modelVersion: string;
  generatedAt: string;
  startGameweek: number;
  startDeadline: string;
  horizon28EndsAt: string;
  gameweeks: Array<{ gameweek: number; deadlineTime: string; daysFromStart: number; inside28Days: boolean; inside5Gw: boolean }>;
  gameweeks28: number[];
  gameweeks5: number[];
  players: Array<{
    elementId: number;
    webName: string;
    teamId: number;
    elementType: number;
    currentPrice: number | null;
    status: string;
    selectedByPercent: number | null;
    byGameweek: HorizonPlayerGameweekProjection[];
    nextGwXpts: number;
    days28Xpts: number;
    fiveGwXpts: number;
  }>;
  methodology: Record<string, string | boolean>;
}

export interface OptimizedRouteResponse {
  routeType: 'ROLL'|'1FT'|'2FT';
  routeLabel: string;
  transfers: Array<{ outElementId: number; outName: string; outElementType:number; outTeamId:number; sellPrice: number; inElementId: number; inName: string; inElementType:number; inTeamId:number; buyPrice: number }>;
  transferCount: number;
  hitCost: number;
  ftAfter: number;
  cashAfter: number;
  nextGwMean: number;
  days28Xpts: number;
  fiveGwXpts: number;
  gain28VsRoll: number;
  formationNextGw: string;
  benchStrengthNextGw: number;
  captainElementId: number;
  viceCaptainElementId: number;
  captainAudit: Array<{ elementId:number; elementType:number; teamId:number; gwXpts:number; pStart:number|null; xMin:number|null; captainScore:number }>;
  minuteRisk: 'LOW'|'MEDIUM'|'HIGH';
  informationRisk: 'LOW'|'MEDIUM'|'HIGH';
  priceRisk: 'LOW'|'MEDIUM'|'HIGH';
  futureFlexibility: 'LOW'|'MEDIUM'|'HIGH';
  priceRouteBuffer: { combinedIncomingRiseBuffer: number | null; closesAtCombinedRise: number | null; label: string };
  decisionTiming: { status: 'MOVE_NOW'|'WAIT_FOR_INFO'|'PRICE_WATCH'; basis: 'QUAL'; expectedInformation: string[]; note: string };
  failureConditions: string[];
  plan: PlannedSquadPayload;
}

export interface RouteOptimizerResponse {
  status: 'PARTIAL';
  horizon: HorizonProjectionResponse;
  optimizer: {
    status: 'PARTIAL';
    optimizerVersion: string;
    generatedAt: string;
    boundedSearch: true;
    maxCandidatesPerPosition: number;
    search: {
      method: 'BOUNDED_MULTI_SIGNAL_CANDIDATE_UNION';
      ownershipUsedAsFilter: false;
      minuteFloorUsedAsFilter: false;
      premiumProtection: false;
      outFinanceCoverage: { known: number; total: number; complete: boolean };
      pools: Array<{
        elementType: number;
        originalPool: number;
        missingPrice: number;
        unavailable: number;
        eligible: number;
        retained: number;
        prunedByBound: number;
        retainedPlayers: Array<{ elementId: number; webName: string; price: number; days28Xpts: number; valuePerMillion: number; reasons: Array<'ABSOLUTE_XPTS'|'VALUE'|'CHEAP_ENABLER'> }>;
        excluded: Array<{ elementId: number; webName: string; reason: string }>;
      }>;
      outgoingSkipped: Array<{ elementId: number; webName: string; reason: 'SELL_PRICE_REQUIRED' }>;
    };
    routes: OptimizedRouteResponse[];
    limitations: string[];
  };
  sequentialBeta: {
    version: string; status: 'BETA'; calibration: 'NOT_YET_CALIBRATED'; startGameweek: number; endGameweek: number;
    comparisonBasis: string; crn: { used: false; note: string };
    bestPath: SequentialRoutePathBeta | null; paths: SequentialRoutePathBeta[]; limitations: string[];
  };
}

export interface SequentialRouteStepBeta {
  gameweek: number; action: 'ROLL'|'1FT'|'2FT'|'RELEVANT_-4'; routeLabel: string; transfers: OptimizedRouteResponse['transfers']; hitCost: number; singleGwEv: number; evBasis:'CURRENT_GW_LINEUP_MEAN_AFTER_HIT';
  ftBefore: number; ftAfterTransfers: number; ftNextGameweek: number; cashBefore: number; cashAfter: number; formation: string; startingGoalkeeper: number | null; startingXI: number[]; benchOrder: number[]; captainElementId: number | null; viceCaptainElementId: number | null; captainAudit:OptimizedRouteResponse['captainAudit']; minuteRisk: 'LOW'|'MEDIUM'|'HIGH'; informationRisk: 'LOW'|'MEDIUM'|'HIGH'; priceRisk: 'LOW'|'MEDIUM'|'HIGH'; failureConditions: string[]; squadElementIds:number[]; legalSquad:true;
}
export interface SequentialRoutePathBeta { pathId:string; firstAction:'ROLL'|'1FT'|'2FT'|'RELEVANT_-4'; steps:SequentialRouteStepBeta[]; singleGwEv:number; cumulative3GwEv:number; cumulative5GwEv:number; futureFt:number; cash:number; minuteRisk:'LOW'|'MEDIUM'|'HIGH'; informationRisk:'LOW'|'MEDIUM'|'HIGH'; priceRisk:'LOW'|'MEDIUM'|'HIGH'; }

export interface SharedWorldComparisonBetaResponse {
  status:'BETA'; label:'BETA · NOT YET CALIBRATED'; gameweek:number; scope:'LIVE'|'NEXT_PREVIEW';
  legacy:{modelVersion:string;a:SimSideSummary;b:SimSideSummary;drawProbability:number};
  sharedWorldBeta:{version:string;status:'BETA';calibration:'NOT_YET_CALIBRATED';iterations:number;seed:string;a:SimSideSummary;b:SimSideSummary;drawProbability:number;assumptions:string[]};
  delta:{aMean:number;bMean:number;aWinProbability:number}; note:string;
}

async function api<T>(url: string): Promise<T> {
  const response = await fetchWithTransientRetry(url, { headers: { accept: 'application/json', ...adminHeaders() } });
  const body: unknown = await response.json();
  if (!response.ok) {
    const message = typeof body === 'object' && body !== null && 'message' in body
      ? String((body as { message: unknown }).message)
      : `HTTP ${response.status}`;
    throw new Error(message);
  }
  return body as T;
}

export const getPublicConfig = () => api<PublicConfigResponse>('/api/config/public');
export const getCurrentGameweek = () => api<CurrentGameweekResponse>('/api/fpl/gameweek/current');
export const getBootstrap = () => api<BootstrapResponse>('/api/fpl/bootstrap');
export const getFixtures = (gw: number) => api<Fixture[]>(`/api/fpl/fixtures?gw=${gw}`);
export const getFeedHealth = () => api<FeedHealthResponse>('/api/feed-health');
export const getLeagueStandings = (leagueId: number, limit = 100) => api<LeagueStandingsResponse>(`/api/fpl/league/${leagueId}/standings?limit=${limit}`);
export const getLatestPlannedSquad = async (entry: string, gw: number) => {
  const url = `/api/bramx/planned-squad?entry=${encodeURIComponent(entry)}&gw=${gw}&_=${Date.now()}`;
  const response = await fetch(url, { cache: 'no-store', headers: { accept: 'application/json', 'cache-control': 'no-cache', ...adminHeaders() } });
  const body: unknown = await response.json();
  if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
  return body as PlannedSquadServerResponse;
};
export const savePlannedSquad = async (entry: string, plan: PlannedSquadPayload) => {
  const response = await fetch('/api/bramx/planned-squad', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json', ...adminHeaders() }, body: JSON.stringify({ entry: Number(entry), plan }) });
  const body: unknown = await response.json();
  if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
  return body as PlannedSquadServerResponse;
};
export const clearPlannedSquad = async (entry: string, gw: number) => {
  const response = await fetch(`/api/bramx/planned-squad?entry=${encodeURIComponent(entry)}&gw=${gw}`, { method: 'DELETE', headers: { accept: 'application/json', ...adminHeaders() } });
  const body: unknown = await response.json();
  if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
  return body as PlannedSquadServerResponse;
};
export const compareEntries = async (entryA: string, entryB: string, gw: number, scope: 'live' | 'preview' = 'live', plannedA?: PlannedSquadPayload | null) => {
  if (plannedA && scope === 'preview') {
    const response = await fetchWithTransientRetry('/api/bramx/compare', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json', ...adminHeaders() }, body: JSON.stringify({ entryA: Number(entryA), entryB: Number(entryB), gw, scope, plannedA }) });
    const body: unknown = await response.json();
    if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
    return body as BramxCompareResponse;
  }
  return api<BramxCompareResponse>(`/api/bramx/compare?entryA=${encodeURIComponent(entryA)}&entryB=${encodeURIComponent(entryB)}&gw=${gw}&scope=${scope}`);
};
export const getMatchCentre = async () => {
  const response = await fetchWithTransientRetry(`/api/match-centre?past=7&ahead=7&_=${Date.now()}`, {
    cache: 'no-store',
    headers: { accept: 'application/json', ...adminHeaders() },
  });
  const body: unknown = await response.json();
  if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
  return body as MatchCentreResponse;
};
export const getProviderFixture = (fixtureId: string) => api<ProviderFixtureBundle>(`/api/fixtures/${encodeURIComponent(fixtureId)}`);

export const replaySimulation = async (runId: string) => {
  const response = await fetch(`/api/bramx/sim-runs/${encodeURIComponent(runId)}/replay`, { headers: { accept: 'application/json', ...adminHeaders() } });
  const body: unknown = await response.json();
  if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
  return body as { exactReplay: boolean; runId: string; originalExecutedAt: string; seed: string | null; inputSnapshotHash: string | null; result: BramxCompareResponse['simulation'] };
};

export const getHorizonProjection = (gw: number) => api<HorizonProjectionResponse>(`/api/bramx/horizon?gw=${gw}`);
export const runRouteOptimizer = async (entry: string, plan: PlannedSquadPayload, maxCandidatesPerPosition = 12) => {
  const response = await fetchWithTransientRetry('/api/bramx/optimizer', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...adminHeaders() },
    body: JSON.stringify({ entry: Number(entry), plan, maxCandidatesPerPosition }),
  });
  const body: unknown = await response.json();
  if (!response.ok) throw new Error(typeof body === 'object' && body !== null && 'message' in body ? String((body as { message: unknown }).message) : `HTTP ${response.status}`);
  return body as RouteOptimizerResponse;
};

export const runSharedWorldBeta = async (entryA:string, entryB:string, gw:number, scope:'live'|'preview', plannedA?:PlannedSquadPayload|null, iterations=2000) => {
  const response = await fetchWithTransientRetry('/api/bramx/shared-world-beta', { method:'POST', headers:{'content-type':'application/json',accept:'application/json',...adminHeaders()}, body:JSON.stringify({entryA:Number(entryA),entryB:Number(entryB),gw,scope,plannedA:plannedA??null,iterations}) });
  const body:unknown=await response.json();
  if(!response.ok) throw new Error(typeof body==='object'&&body!==null&&'message' in body?String((body as {message:unknown}).message):`HTTP ${response.status}`);
  return body as SharedWorldComparisonBetaResponse;
};

export const getLatestDecisionSnapshot = async (entry:string, gw:number) => api<{configured:boolean;snapshot:any|null}>(`/api/bramx/decision-snapshot/latest?entry=${encodeURIComponent(entry)}&gw=${gw}`);
export const captureLearningOutcomes = async (entry:string, gw:number) => {
  const response=await fetch('/api/bramx/learning/capture-live',{method:'POST',headers:{'content-type':'application/json',accept:'application/json',...adminHeaders()},body:JSON.stringify({entry:Number(entry),gw})});
  const body:unknown=await response.json();
  if(!response.ok) throw new Error(typeof body==='object'&&body!==null&&'message' in body?String((body as {message:unknown}).message):`HTTP ${response.status}`);
  return body as {snapshotFound:boolean;created:number;skipped:number;playerOutcomes:number;marketOutcomes:number;sourceOutcomes:number;note:string};
};

export type DecisionSnapshotType = 'POST_GW'|'T-72'|'T-24'|'T-6'|'T-1'|'DEADLINE';
export const captureDecisionCheckpoint = async (entry:string, gw:number, snapshotType:DecisionSnapshotType) => {
  const response=await fetch('/api/bramx/capture/checkpoint',{method:'POST',headers:{'content-type':'application/json',accept:'application/json',...adminHeaders()},body:JSON.stringify({entry:Number(entry),gw,snapshotType,targetAt:new Date().toISOString()})});
  const body:unknown=await response.json();
  if(!response.ok) throw new Error(typeof body==='object'&&body!==null&&'message' in body?String((body as {message:unknown}).message):`HTTP ${response.status}`);
  return body as {snapshotType:DecisionSnapshotType;targetAt:string;market:{ok:boolean};elite:{ok:boolean};decision:any};
};
